import type { PlanChangeConfirmationModalProps } from "@v2/types/modalTypes";
import type { AddOn } from "schemas/generated/addOns";

import * as React from "react";

import { Alert } from "@replohq/lattice/blocks/alert/Alert";
import { SplitModal } from "@replohq/lattice/blocks/modal/SplitModal";
import { Button } from "@replohq/lattice/components/button/Button";
import { Drawer } from "@replohq/lattice/components/drawer/Drawer";
import { Separator } from "@replohq/lattice/components/separator/Separator";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@replohq/lattice/components/tabs/Tabs";
import { Tag } from "@replohq/lattice/components/tag/Tag";
import { useIsMobile } from "@replohq/lattice/hooks/use-mobile";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import { showSubscriptionUpdateSuccessToast } from "@v2/features/billing/billingToasts";
import useApplySubscriptionChangeMutation from "@v2/features/billing/hooks/useApplySubscriptionChangeMutation";
import {
  getActiveSubscriptionPricePerMonth,
  getEstimatedPlanPricePerMonth,
  getPercentSavedBySwitchingToYearly,
} from "@v2/features/billing/planPriceUtils";
import {
  getAddOnDisplayInfo,
  getPlanRank,
} from "@v2/features/billing/shared/sharedBillingUtils";
import { useSubscriptionDetails } from "@v2/hooks/subscription";
import { billingRouter } from "@v2/trpc/routes/billing";
import { ArrowRight } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { DEPRECATED_formatPriceInUsdDollars } from "replo-utils/lib/format";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { getBillingPlanInfoByTier } from "schemas/billing";

export const PlanChangeConfirmationModal: React.FC<
  PlanChangeConfirmationModalProps
> = ({
  newTier,
  newBillingInterval,
  onBillingIntervalChange,
  addOns,
  open,
  onOpenChange,
}) => {
  const isMobile = useIsMobile();
  const workspace = useCurrentWorkspace();

  const [billingInterval, setBillingInterval] = useOverridableState<
    "monthly" | "yearly"
  >(newBillingInterval, onBillingIntervalChange);

  const { applySubscriptionChange, isLoading: isLoadingSubscriptionUpdate } =
    useApplySubscriptionChangeMutation({
      onClose: () => onOpenChange(false),
    });

  const { data: activeSubscription } = useSubscriptionDetails();

  const { data: nextBillingDate } =
    billingRouter.getNextCorePlanBillingDate.useQuery(
      workspace && activeSubscription?.id
        ? {
            workspaceId: workspace.id,
            subscriptionId: activeSubscription.id,
          }
        : skipToken,
    );

  const formattedNextBillingDate = React.useMemo(() => {
    if (nextBillingDate) {
      return new Date(nextBillingDate).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
    const futureDate = new Date();
    if (billingInterval === "yearly") {
      futureDate.setFullYear(futureDate.getFullYear() + 1);
    } else {
      futureDate.setMonth(futureDate.getMonth() + 1);
    }
    return futureDate.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [nextBillingDate, billingInterval]);

  if (!workspace) {
    return null;
  }

  const modalGraphicSrc =
    newTier === "starter"
      ? "/images/billing/starter-modal-graphic.png"
      : "/images/billing/pro-modal-graphic.png";

  const currentTier = activeSubscription?.billingTier ?? "free";

  const currentTierRank = getPlanRank(currentTier);
  const newTierRank = getPlanRank(newTier);

  const isPlanCanceled = Boolean(activeSubscription?.endedAt);

  let modalType:
    | "upgrade"
    | "downgrade"
    | "update-configuration"
    | "resume-plan" = "update-configuration";

  if (isPlanCanceled) {
    modalType = "resume-plan";
  } else if (currentTierRank < newTierRank) {
    modalType = "upgrade";
  } else if (currentTierRank > newTierRank) {
    modalType = "downgrade";
  }

  const isAnnualPlanAlertVisible =
    activeSubscription?.billingInterval === "monthly" &&
    billingInterval === "yearly";

  const newPlanInfo = getBillingPlanInfoByTier(newTier);

  const newCorePlanPricePerMonth =
    billingInterval === "yearly"
      ? newPlanInfo.yearlyPricePerMonth
      : newPlanInfo.monthlyPrice;

  const handleSubmitPlanChanges = async () => {
    await applySubscriptionChange({
      workspaceId: workspace.id,
      billingTier: newTier,
      billingInterval,
      addOns,
      source: "direct.billingDashboard",
    });
  };

  const handleConfirm = async () => {
    await handleSubmitPlanChanges();
    showSubscriptionUpdateSuccessToast();
    onOpenChange(false);
  };

  const currentPlanTotalPricePerMonthIncludingAllProducts = activeSubscription
    ? getActiveSubscriptionPricePerMonth({
        activeSubscription,
      })
    : 0;

  const newPlanTotalPricePerMonthIncludingAllProducts =
    getEstimatedPlanPricePerMonth({
      billingTier: newTier,
      billingInterval,
      addOns,
    });

  const headerText = exhaustiveSwitch({ type: modalType })({
    upgrade: (
      <>
        Level up to <span className="italic">{newPlanInfo.displayName}</span>
      </>
    ),
    downgrade: (
      <>
        Downgrade to <span className="italic">{newPlanInfo.displayName}</span>
      </>
    ),
    "update-configuration": (
      <>
        Update your <span className="italic">{newPlanInfo.displayName}</span>{" "}
        plan
      </>
    ),
    "resume-plan": (
      <>
        Resume your <span className="italic">{newPlanInfo.displayName}</span>{" "}
        plan
      </>
    ),
  });

  const sublineItems = addOns
    .filter((addOn: AddOn) => addOn.quantity > 0)
    .map((addOn: AddOn) => {
      const { label, pricePerMonth } = getAddOnDisplayInfo({
        type: addOn.type,
        quantity: addOn.quantity,
      });
      return {
        label: `${addOn.quantity} Additional ${label}`,
        price: addOn.quantity * pricePerMonth,
      };
    });

  const formattedTotalPrice =
    billingInterval === "yearly"
      ? `${DEPRECATED_formatPriceInUsdDollars({ amount: newPlanTotalPricePerMonthIncludingAllProducts * 12, currency: "usd" })}/yr`
      : `${DEPRECATED_formatPriceInUsdDollars({ amount: newPlanTotalPricePerMonthIncludingAllProducts, currency: "usd" })}/mo`;

  const confirmButtonText = exhaustiveSwitch({ type: modalType })({
    upgrade: `Confirm Upgrade (${formattedTotalPrice})`,
    downgrade: `Confirm Downgrade (${formattedTotalPrice})`,
    "update-configuration": `Confirm Changes (${formattedTotalPrice})`,
    "resume-plan": `Resume Plan (${formattedTotalPrice})`,
  });

  const percentSavedBySwitchingToYearly = getPercentSavedBySwitchingToYearly({
    billingTier: newTier,
    addOns,
  });

  const billingIntervalTabs = (
    <Tabs
      value={billingInterval}
      onValueChange={(value) => {
        // Legacy cast - may or may not be necessary
        // eslint-disable-next-line replo/no-as-cast
        return setBillingInterval(value as "monthly" | "yearly");
      }}
      className="max-w-64"
    >
      <TabsList
        size="base"
        UNSAFE_className="rounded-full [&>span]:rounded-full"
      >
        <TabsTrigger value="yearly" UNSAFE_className="rounded-full">
          Annual ({percentSavedBySwitchingToYearly}% Off)
        </TabsTrigger>
        <TabsTrigger value="monthly" UNSAFE_className="rounded-full">
          Monthly
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );

  const pricingBreakdown = (
    <div className="flex flex-col gap-4">
      <div className="text-[32px] font-editorial">{headerText}</div>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="text-sm font-medium">
                {newPlanInfo.displayName} Plan
              </div>
              <Tag size="sm" variant="v2">
                {billingInterval === "yearly" ? "Annual" : "Monthly"}
              </Tag>
            </div>
            <div className="text-base font-semibold">
              {DEPRECATED_formatPriceInUsdDollars({
                amount: newCorePlanPricePerMonth,
                currency: "usd",
              })}
              /mo
            </div>
          </div>
          <div className="text-xs text-muted">
            Renews {formattedNextBillingDate}
          </div>
        </div>
        {sublineItems.length > 0 && (
          <div className="flex flex-col gap-2">
            {sublineItems.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between text-xs"
              >
                <div className="text-muted">{item.label}</div>
                <div className="font-semibold">
                  {DEPRECATED_formatPriceInUsdDollars({
                    amount: item.price,
                    currency: "usd",
                  })}
                  /mo
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Separator className="border-t border-dashed border-border bg-transparent" />
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-muted">Subtotal</div>
        <div className="flex items-center gap-2">
          <div className="text-base font-medium text-muted">
            {DEPRECATED_formatPriceInUsdDollars({
              amount: currentPlanTotalPricePerMonthIncludingAllProducts,
              currency: "usd",
            })}
            /mo
          </div>
          <ArrowRight className="text-muted" size={18} />
          <div className="text-base font-semibold">
            {DEPRECATED_formatPriceInUsdDollars({
              amount: newPlanTotalPricePerMonthIncludingAllProducts,
              currency: "usd",
            })}
            /mo
          </div>
        </div>
      </div>
    </div>
  );

  const footer = (
    <div className="flex flex-col gap-2">
      {isAnnualPlanAlertVisible && (
        <Alert
          variant="warning"
          description="You're switching to an annual plan. Please note this plan is not refundable."
        />
      )}
      <Button
        variant="primary"
        size="lg"
        className="w-full"
        onClick={() => void handleConfirm()}
        loading={isLoadingSubscriptionUpdate}
      >
        {confirmButtonText}
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} footer={footer}>
        <div className="flex flex-col gap-4">
          <Tabs
            value={billingInterval}
            onValueChange={(value) => {
              // Legacy cast - may or may not be necessary
              // eslint-disable-next-line replo/no-as-cast
              return setBillingInterval(value as "monthly" | "yearly");
            }}
            className="w-full"
          >
            <TabsList
              size="base"
              className="w-full"
              UNSAFE_className="rounded-full [&>span]:rounded-full"
            >
              <TabsTrigger value="yearly" UNSAFE_className="rounded-full">
                Annual ({percentSavedBySwitchingToYearly}% Off)
              </TabsTrigger>
              <TabsTrigger value="monthly" UNSAFE_className="rounded-full">
                Monthly
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {pricingBreakdown}
          <img
            src={modalGraphicSrc}
            alt={`${newPlanInfo.displayName} plan graphic`}
            className="w-full h-64 object-cover rounded-lg"
          />
        </div>
      </Drawer>
    );
  }

  return (
    <SplitModal
      open={open}
      onOpenChange={onOpenChange}
      media={
        <img
          src={modalGraphicSrc}
          alt={`${newPlanInfo.displayName} plan graphic`}
          className="h-full w-full object-cover"
        />
      }
    >
      <div className="flex flex-col gap-4 mb-4 pr-12">
        {billingIntervalTabs}
      </div>
      <div className="flex flex-1 flex-col justify-between">
        {pricingBreakdown}
        {footer}
      </div>
    </SplitModal>
  );
};
